import { RequestContext } from "asosteam";
import { parse } from "cookie";
import type { AppProps, AppContext } from "next/app";
import App, { AppInitialProps } from "next/app";
import {
  getNavigation,
  type NavigationResponse,
} from "@repo/apis/browse-navigation-v2";
import { BrowseSearchV2 } from "@repo/apis/browse-search-v2";
import { SitecorePageProps } from "@repo/sitecore-lib/page-props";
import { getSiteBanner } from "@repo/sitecore-lib/queries";
import {
  setAuthoringShopHeaders,
  getRequestContext,
} from "@repo/sitecore-lib/store-presentation";
import { PageLoadAnalytics } from "@/components/analytics/page-load-analytics";
import { Footer } from "@/components/footer/footer";
import { getBannerContentFromResponse } from "@/components/global-banner/utils";
import { Nav } from "@/components/nav";
import { SecureScriptContainerClient } from "@/components/secure-script-container/secure-script-container-client";
import { SearchProvider } from "@/components/sitecore-components";
import { shouldShowHeroHeader } from "@/components/sitecore-components/layouts/utils";
import BrandLogo from "@/components/topshop-landing/brand-logo";
import { useShouldShowOverlay } from "@/components/topshop-landing/hooks";
import { relativePro } from "@/theme/fonts";
import { SpinnerIcon } from "@/theme/icons/spinner";
import { configurePageRouterServerRequest } from "@/utils/request-adapters/page-router-server-request";
import { Client } from "../app/client";
import "@/theme/topshop/index.css";
import "@/components/sitecore-components/layouts/sitecore-global.css";

export type HeadersType = {
  headers: {
    isEditing: boolean;
    requestContext: RequestContext;
    navigation: NavigationResponse | null;
    trendingCategoryProducts: BrowseSearchV2 | null;
    bannerContent: Awaited<ReturnType<typeof getBannerContentFromResponse>>;
  };
  requestContext: RequestContext;
  isLanding?: boolean;
};

export type AppPageProps = SitecorePageProps & HeadersType;

function TopshopApp({
  Component,
  pageProps,
}: AppProps<AppPageProps>): JSX.Element {
  const { showLogoOverlay, isOverlayReady, activeFloor } =
    useShouldShowOverlay();

  const { headers, layoutData, requestContext, isLanding } = pageProps;
  const { navigation, isEditing, bannerContent } = headers;

  if (!headers || (!isEditing && !isOverlayReady)) {
    return <SpinnerIcon label="Loading..." />;
  }

  const isContentNavTransparent = layoutData
    ? shouldShowHeroHeader(layoutData)
    : false;

  const {
    countryData,
    keyStoreDataversion,
    shop,
    versionManifest,
    platform,
    lang,
  } = requestContext;

  return (
    <main className={relativePro.className}>
      <Client
        countryData={countryData}
        keyStoreDataversion={keyStoreDataversion}
        shop={shop}
        versionManifest={versionManifest}
        lang={lang}
        platform={platform}
      >
        {!isLanding && !isEditing && (
          <Nav
            initialNavigation={navigation}
            isContentNavTransparent={isContentNavTransparent}
            requestContext={requestContext}
            bannerContent={bannerContent}
          />
        )}
        {showLogoOverlay && !isEditing && (
          <BrandLogo activeFloor={activeFloor!} />
        )}
        <SearchProvider>
          <Component {...pageProps} activeFloor={activeFloor!} />
        </SearchProvider>
        {!isLanding && !isEditing && <Footer />}
        <PageLoadAnalytics
          browseCurrency={countryData.defaultCurrency}
          browseCountry={countryData.countryCode}
          language={countryData.defaultLanguage}
          channel="default"
          platform={platform}
        />
        <SecureScriptContainerClient />
      </Client>
    </main>
  );
}

TopshopApp.getInitialProps = async (
  appContext: AppContext,
): Promise<AppInitialProps & { pageProps: AppPageProps }> => {
  const appProps = await App.getInitialProps(appContext);

  const req = appContext.ctx.req;

  if (!req) {
    return appProps as AppInitialProps & { pageProps: AppPageProps };
  }

  let isEditing = false;

  if (req.headers.cookie) {
    const parsedCookies = parse(req.headers.cookie);
    isEditing = parsedCookies.isEditing === "true";
  }

  setAuthoringShopHeaders(req);

  const requestContext = await getRequestContext(req);

  let navigation: NavigationResponse | null = null;
  let bannerContent: Awaited<ReturnType<typeof getBannerContentFromResponse>> =
    [];

  if (!isEditing) {
    const serverRequest = configurePageRouterServerRequest({ req });

    const [navResult, bannerResult] = await Promise.allSettled([
      getNavigation({
        requestInstance: serverRequest,
        requestContext,
      }),
      getSiteBanner({
        requestInstance: serverRequest,
        shop: requestContext.shop,
        language: requestContext.countryData.defaultLanguage,
      }),
    ]);

    if (navResult.status === "fulfilled") {
      navigation = navResult.value;
    } else {
      console.error("Failed to fetch navigation:", navResult.reason);
    }

    if (bannerResult.status === "fulfilled") {
      bannerContent = getBannerContentFromResponse(bannerResult.value);
    } else {
      console.error("Failed to fetch banner:", bannerResult.reason);
    }
  }

  return {
    ...appProps,
    pageProps: {
      ...appProps.pageProps,
      headers: {
        isEditing,
        navigation,
        bannerContent,
      },
      requestContext,
    },
  };
};

export default TopshopApp;
